import { TableStatusBreakdownCell } from '@/features/shared/components/tableSystem/TableStatusBreakdownCell';
import { resolveEntityTags } from '@/features/shared/tags';
import { featureStatusOptions } from '@/shared/constants/statusOptions';
import { formatStatusLabel, getStatusTextColor, Text } from '@/ui';
import {
  getSingleSelectOptionLabel,
  getSingleSelectOptionValue,
} from '@/ui/composites/grid/gridColumns';
import {
  getSingleSelectFilterOperators,
  getStringFilterOperators,
} from '@/ui/composites/grid/gridOperators';
import { TableTextCell, TagsInlineCell } from '@/ui/composites/table';
import type { GridColDef } from '@/ui/primitives';
import type { FeatureDto } from '@flowqa/shared';

type FeaturesColumnsOptions = {
  onProjectSelect: (projectId: string) => void;
};

const columnFlex = { name: 1, status: 0.25, suites: 1, tests: 1, tags: 0.75 };

export const buildFeaturesColumns = ({
  onProjectSelect,
}: FeaturesColumnsOptions): Array<GridColDef<FeatureDto>> => {
  const stringOperators = getStringFilterOperators();
  const singleSelectOperators = getSingleSelectFilterOperators();
  const nameColumn: GridColDef<FeatureDto> = {
    field: 'name',
    headerName: 'Feature',
    flex: columnFlex.name,
    valueGetter: (_value, row) => row.name,
    renderCell: (params) => (
      <TableTextCell
        primary={{
          label: params.row.name,
          href: `/features/${params.row.slug}`,
          onClick: () => {
            onProjectSelect(params.row.projectId);
          },
        }}
      />
    ),
  };
  nameColumn.filterOperators = stringOperators;

  const statusColumn: GridColDef<FeatureDto> = {
    field: 'status',
    headerName: 'Status',
    flex: columnFlex.status,
    align: 'center',
    headerAlign: 'center',
    type: 'singleSelect',
    valueOptions: featureStatusOptions,
    getOptionLabel: getSingleSelectOptionLabel,
    getOptionValue: getSingleSelectOptionValue,
    filterOperators: singleSelectOperators,
    renderCell: (params) => {
      const status = params.row.status;
      const label = formatStatusLabel(status);
      return (
        <Text
          role="body"
          component="span"
          sx={{
            color: (theme) => getStatusTextColor(theme, status),
          }}
          transform="uppercase"
        >
          {label.length > 0 ? label : '-'}
        </Text>
      );
    },
  };

  const suitesColumn: GridColDef<FeatureDto> = {
    field: 'suiteLatestExecutionPassed',
    headerName: 'Latest Suites',
    flex: columnFlex.suites,
    align: 'center',
    headerAlign: 'center',
    filterable: false,
    sortable: false,
    renderCell: (params) => {
      const counts = {
        passed: params.row.suiteLatestExecutionPassed ?? 0,
        failed: params.row.suiteLatestExecutionFailed ?? 0,
        errored: params.row.suiteLatestExecutionErrored ?? 0,
        skipped: params.row.suiteLatestExecutionSkipped ?? 0,
        untested: params.row.suiteLatestExecutionUntested ?? 0,
      };
      return <TableStatusBreakdownCell entity="suite" counts={counts} />;
    },
  };

  const testsColumn: GridColDef<FeatureDto> = {
    field: 'testLatestExecutionPassed',
    headerName: 'Latest Tests',
    flex: columnFlex.tests,
    align: 'center',
    headerAlign: 'center',
    filterable: false,
    sortable: false,
    renderCell: (params) => {
      const counts = {
        passed: params.row.testLatestExecutionPassed ?? 0,
        failed: params.row.testLatestExecutionFailed ?? 0,
        errored: params.row.testLatestExecutionErrored ?? 0,
        skipped: params.row.testLatestExecutionSkipped ?? 0,
        untested: params.row.testLatestExecutionUntested ?? 0,
      };
      return <TableStatusBreakdownCell entity="test" counts={counts} />;
    },
  };

  const tagsColumn: GridColDef<FeatureDto> = {
    field: 'tags',
    headerName: 'Tags',
    flex: columnFlex.tags,
    filterable: false,
    sortable: false,
    renderCell: (params) => <TagsInlineCell tags={resolveEntityTags(params.row)} />,
  };

  return [nameColumn, statusColumn, suitesColumn, testsColumn, tagsColumn];
};
